import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { QuizSlot, TopicQuizConfig } from '../../types/topicQuiz';
import {
  createDefaultQuiz,
  formatQuizDurationSeconds,
  getMaxLevelQuestionCount,
  getQuizFixedQuestionCount,
  getQuizDurationSeconds,
  isQuizWithinTimeLimit,
  normalizeQuizConfig,
  QUIZ_SECONDS_BY_LEVEL,
} from '../../types/topicQuiz';

interface TopicQuizModalProps {
  open: boolean;
  topicId: string;
  slot: QuizSlot;
  topicTitle: string;
  topicIndex: number;
  initialConfig?: TopicQuizConfig;
  secondsByLevel?: Record<1 | 2 | 3, number>;
  onClose: () => void;
  onSave: (config: TopicQuizConfig) => void;
}

export function TopicQuizModal({
  open,
  slot,
  topicTitle,
  topicIndex,
  initialConfig,
  secondsByLevel = QUIZ_SECONDS_BY_LEVEL,
  onClose,
  onSave,
}: TopicQuizModalProps) {
  const [config, setConfig] = useState<TopicQuizConfig>(() =>
    ensureLevelRules(initialConfig, slot),
  );

  useEffect(() => {
    if (open) {
      setConfig(ensureLevelRules(initialConfig, slot));
    }
  }, [open, initialConfig, slot]);

  if (!open) return null;

  const slotLabel = slot === 'pre' ? 'Pre-Quiz' : 'Post-Quiz';
  const targetCount = getQuizFixedQuestionCount(slot);
  const questionCount =
    config.levelRules?.reduce((sum, rule) => sum + rule.questionCount, 0) ?? 0;
  const countComplete = questionCount === targetCount;
  const quizSeconds = getQuizDurationSeconds(config, []);
  const canSave = isQuizWithinTimeLimit(config, []);

  const updateLevelCount = (level: 1 | 2 | 3, count: number) => {
    setConfig((prev) => {
      const rules = prev.levelRules ?? emptyRules();
      const maxForLevel = getMaxLevelQuestionCount(level, { ...prev, levelRules: rules });
      const nextCount = Math.min(Math.max(0, count), maxForLevel);
      return {
        ...prev,
        mode: 'level-rules',
        selectedQuestionIds: undefined,
        levelRules: rules.map((rule) =>
          rule.level === level ? { ...rule, questionCount: nextCount } : rule,
        ),
      };
    });
  };

  const handleSave = () => {
    if (!countComplete) return;
    onSave(
      normalizeQuizConfig(
        {
          ...config,
          slot,
          mode: 'level-rules',
          selectedQuestionIds: undefined,
        },
        [],
      ),
    );
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl"
        role="dialog"
        aria-labelledby="quiz-modal-title"
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 id="quiz-modal-title" className="text-lg font-bold text-slate-900">
              {slotLabel} rules (all sessions)
            </h2>
            <p className="text-sm text-slate-500">
              One shared rule set for every session and topic
            </p>
            <p
              className={`mt-1 text-sm font-semibold ${countComplete ? 'text-emerald-600' : 'text-amber-600'}`}
            >
              {questionCount}/{targetCount} questions
              {quizSeconds > 0 ? ` · ~${formatQuizDurationSeconds(quizSeconds)}` : ''}
              {!countComplete ? ` · assign ${targetCount - questionCount} more` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-4">
          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">
              Set question count per level ({targetCount} questions total)
            </p>
            <p className="mb-3 text-xs text-slate-500">
              Changing these rules updates pre/post quizzes for all sessions and topics.
            </p>
            <div className="space-y-3">
              {([1, 2, 3] as const).map((level) => {
                const rule = config.levelRules?.find((r) => r.level === level);
                const count = rule?.questionCount ?? 0;
                const maxForLevel = getMaxLevelQuestionCount(level, config);
                const atMax = count >= maxForLevel;
                const seconds = secondsByLevel[level] ?? QUIZ_SECONDS_BY_LEVEL[level];
                return (
                  <div
                    key={level}
                    className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3"
                  >
                    <span className="font-medium text-slate-800">
                      Level {level}
                      <span className="ml-1 text-xs font-normal text-slate-500">
                        ({seconds}s per question)
                      </span>
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateLevelCount(level, count - 1)}
                        className="h-8 w-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                      >
                        −
                      </button>
                      <span className="w-8 text-center font-semibold">{count}</span>
                      <button
                        type="button"
                        onClick={() => updateLevelCount(level, count + 1)}
                        disabled={atMax}
                        className="h-8 w-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Save shared rules
          </button>
        </div>
      </div>
    </div>
  );
}

function emptyRules() {
  return [
    { level: 1 as const, questionCount: 0 },
    { level: 2 as const, questionCount: 0 },
    { level: 3 as const, questionCount: 0 },
  ];
}

function ensureLevelRules(
  initialConfig: TopicQuizConfig | undefined,
  slot: QuizSlot,
): TopicQuizConfig {
  if (!initialConfig) return createDefaultQuiz(slot);
  return {
    ...initialConfig,
    slot,
    mode: 'level-rules',
    selectedQuestionIds: undefined,
    levelRules: initialConfig.levelRules?.length
      ? initialConfig.levelRules
      : createDefaultQuiz(slot).levelRules,
  };
}
